import { IPoint2d } from '@/editor/math/IPoint2d';
import { pixelRatio2d } from '@/editor/math/const';
import {
  ENTITY_RENDER_ORDER,
  filledCircleColor,
  selectedOutlineColor,
  DefaultEntitySize
} from '@/editor/constant/entity';
import BaseEntity from './BaseEntity';
import { disposeAllChildrenThenRemoveFromParent } from './utils';
import { EEntityStatus } from '../types/entity';
import { getCircleContour } from '../utils/face';
import { CircleGeometry, Group, Mesh, MeshBasicMaterial, Vector3 } from 'three';
import { Line2, LineGeometry } from 'three/examples/jsm/Addons.js';
import { globalStores } from '../stores';
import { MaterialStore } from '../stores/materialStore';

export class AreaDimensionEndPointEntity extends BaseEntity {
  private _pointGroup: Group = new Group();
  private _hollowCircle: Line2 | null = null;
  private _filledCircle: Mesh | null = null;
  private _position: IPoint2d = { x: 0, y: 0 };
  private _scale: number = 1;
  private _modelCreated: boolean = false;

  static normalMaterial = new MeshBasicMaterial({ color: filledCircleColor, transparent: true, opacity: 1 });
  static highlightMaterial = new MeshBasicMaterial({ color: selectedOutlineColor, transparent: true, opacity: 1 });

  status: EEntityStatus = EEntityStatus.normal;

  constructor() {
    super();
    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._pointGroup);

    this.setDisplayOrder(ENTITY_RENDER_ORDER.transformHelper);
  }

  get position() {
    return this._position;
  }

  private createModel(theme: 'normal' | 'highlight' = 'normal') {
    const radius = DefaultEntitySize.WallExtremePointRadius;
    const circlePoints = getCircleContour({ x: 0, y: 0 }, radius, 32);
    circlePoints.push(circlePoints[0]);
    const circleGeometry = new LineGeometry().setFromPoints(
      circlePoints.map((point) => new Vector3(point.x / pixelRatio2d, point.y / pixelRatio2d, 0.001))
    );
    this._hollowCircle = new Line2(circleGeometry, MaterialStore.sharedMaterials.extremePointOutline);
    this._hollowCircle.renderOrder = ENTITY_RENDER_ORDER.transformHelper;

    const filledGeometry = new CircleGeometry(radius / pixelRatio2d, 32);
    this._filledCircle = new Mesh(
      filledGeometry,
      theme === 'normal' ? AreaDimensionEndPointEntity.normalMaterial : AreaDimensionEndPointEntity.highlightMaterial
    );
    this._filledCircle.renderOrder = ENTITY_RENDER_ORDER.transformHelper;

    this._pointGroup.add(this._filledCircle, this._hollowCircle);
    this._modelCreated = true;
  }

  updatePoint(position: IPoint2d, scale: number, theme: 'normal' | 'highlight' = 'normal') {
    if (!this._modelCreated) this.createModel(theme);

    this._position = position;
    this._scale = scale;

    this._pointGroup.position.setX(this._position.x / pixelRatio2d);
    this._pointGroup.position.setY(this._position.y / pixelRatio2d);
    this._pointGroup.scale.set(this._scale, this._scale, 1);
  }

  setDisplayOrder(order: number) {
    this._pointGroup.position.z = order;
  }

  // 动态调整圆形的 scale 以抵消 zoom
  updateScale(scale: number) {
    this._pointGroup.scale.set(scale, scale, 1);
  }

  override dispose() {
    this._modelCreated = false;
    disposeAllChildrenThenRemoveFromParent(this._pointGroup);
  }
}
